/**
 * Pure utility functions for the MCP key feature (RITA-1369).
 * Extracted here so they can be unit-tested without mounting React components.
 */

/**
 * Groups flat scope strings like ['Belege:lesen', 'Belege:schreiben', 'Audit:lesen']
 * into { Belege: ['Belege:lesen', 'Belege:schreiben'], Audit: ['Audit:lesen'] }.
 */
export function groupScopesByPrefix(scopes: string[]): Record<string, string[]> {
  return scopes.reduce<Record<string, string[]>>((acc, scope) => {
    const colonIdx = scope.indexOf(':');
    const prefix = colonIdx >= 0 ? scope.slice(0, colonIdx) : scope;
    const existing = acc[prefix] ?? [];
    return { ...acc, [prefix]: [...existing, scope] };
  }, {});
}

// CONTRACT: One source of truth for scope grouping and display labels (RITA-1386).
// Unknown scopes render in 'Sonstige' — never silently dropped (fallback-contract).
export interface ScopeMetadata {
  displayName: string;
  description: string;
  group: string;
}

export const SCOPE_METADATA: Record<string, ScopeMetadata> = {
  'Belege:lesen': { displayName: 'Belege lesen', description: 'Belege auflisten & einzelne abrufen', group: 'Belege' },
  'Belege:schreiben': { displayName: 'Belege schreiben', description: 'Belege anlegen & ändern', group: 'Belege' },
  'Berater:lesen': { displayName: 'Berater lesen', description: 'Berater per Name finden', group: 'Stammdaten' },
  'Berater:profil-lesen': {
    displayName: 'Berater-Profil lesen',
    description: 'Vollprofil & gefilterte Beraterlisten (personenbezogene Daten)',
    group: 'Stammdaten',
  },
  'Berater:schreiben': {
    displayName: 'Berater schreiben',
    description: 'Berater-Stammdaten ändern & Notizen anlegen',
    group: 'Stammdaten',
  },
  'Leistungen:lesen': {
    displayName: 'Leistungen lesen',
    description: 'Leistungen & Servicekatalog abrufen',
    group: 'Leistungen',
  },
  'Leistungen:schreiben': {
    displayName: 'Leistungen schreiben',
    description: 'Leistungen anlegen & beenden',
    group: 'Leistungen',
  },
  'Audit:global-lesen': {
    displayName: 'Audit global lesen',
    description: 'Operations-Audit aller Nutzer abrufen',
    group: 'Audit',
  },
  'Buchungskonten:lesen': {
    displayName: 'Buchungskonten lesen',
    description: 'Kontenrahmen nachschlagen',
    group: 'Stammdaten',
  },
  'Steuersätze:lesen': {
    displayName: 'Steuersätze lesen',
    description: 'Steuersätze nachschlagen',
    group: 'Stammdaten',
  },
  'Audit:lesen': { displayName: 'Audit lesen', description: 'Eigene Audit-Einträge abrufen', group: 'Audit' },
};

export const SCOPE_GROUP_ORDER: readonly string[] = ['Belege', 'Stammdaten', 'Leistungen', 'Audit'];

export interface GroupedScopeItem {
  scope: string;
  displayName: string;
  description: string;
}

export interface ScopeGroup {
  group: string;
  items: GroupedScopeItem[];
}

export function groupScopesWithMetadata(scopes: string[]): ScopeGroup[] {
  const groupMap: Record<string, GroupedScopeItem[]> = {};
  scopes.forEach((scope) => {
    const meta = SCOPE_METADATA[scope];
    const group = meta?.group ?? 'Sonstige';
    const displayName = meta?.displayName ?? scope.replace(':', ' ');
    const description = meta?.description ?? '';
    if (!groupMap[group]) {
      groupMap[group] = [];
    }
    groupMap[group].push({ scope, displayName, description });
  });
  const knownGroups = SCOPE_GROUP_ORDER.reduce<ScopeGroup[]>((acc, g) => {
    if (groupMap[g]) acc.push({ group: g, items: groupMap[g] });
    return acc;
  }, []);
  return groupMap.Sonstige ? [...knownGroups, { group: 'Sonstige', items: groupMap.Sonstige }] : knownGroups;
}

/** Returns the human-readable display name for a scope (admin chips + consistent labels). */
export function getScopeDisplayName(scope: string): string {
  return SCOPE_METADATA[scope]?.displayName ?? scope.replace(':', ' ');
}

// CONTRACT §13 (RITA-1393/1400/1454): Schreib-Scopes implizieren ihre Pflicht-Lookup-Lese-Scopes.
// Spiegel von SCOPE_IMPLICATIONS im Backend (mcp/scopes.ts, durchgesetzt in createMcpKey.ts).
// Berater:profil-lesen (PII-Vollprofil) ist BEWUSST in keiner Regel enthalten.
export const SCOPE_IMPLICATIONS: ReadonlyArray<readonly [string, readonly string[]]> = [
  ['Belege:schreiben', ['Buchungskonten:lesen', 'Steuersätze:lesen', 'Berater:lesen']],
  ['Leistungen:schreiben', ['Leistungen:lesen', 'Berater:lesen']],
  ['Berater:schreiben', ['Berater:lesen']],
];

/**
 * CONTRACT §13/§13b (pure Funktion, verhaltensgetestet — RITA-1454):
 * - Einschalten eines Trigger-Scopes waehlt dessen implizierte Scopes automatisch mit (§13).
 * - Ein implizierter Scope kann NICHT abgewaehlt werden, solange IRGENDEIN aktiver Trigger
 *   ihn impliziert (§13b) — die UI zeigt exakt die Scopes, die das Backend immer erteilt.
 * - Ausschalten eines Triggers entfernt implizierte Scopes NICHT (koennen eigenstaendig
 *   gewaehlt sein).
 */
export function toggleScopeSelection(prev: string[], scope: string): string[] {
  if (prev.includes(scope)) {
    const lockedByActiveTrigger = SCOPE_IMPLICATIONS.some(
      ([trigger, implied]) => prev.includes(trigger) && implied.includes(scope),
    );
    if (lockedByActiveTrigger) {
      return prev;
    }
    return prev.filter((s: string) => s !== scope);
  }
  const next = [...prev, scope];
  const rule = SCOPE_IMPLICATIONS.find(([trigger]) => trigger === scope);
  if (rule) {
    const implied = rule[1].filter((s) => !next.includes(s));
    return [...next, ...implied];
  }
  return next;
}

export type ExpiryOption = 'none' | '30d' | '90d' | 'custom';

/**
 * Converts an expiry option + optional custom date into an ISO date string (YYYY-MM-DD)
 * or null (= no expiry). The today parameter is injected so it can be pinned in tests.
 */
export function getExpiresAt(option: ExpiryOption, customDate: string, today: Date): string | null {
  if (option === 'none') return null;
  if (option === 'custom') return customDate || null;
  const d = new Date(today);
  if (option === '30d') d.setDate(d.getDate() + 30);
  if (option === '90d') d.setDate(d.getDate() + 90);
  // CONTRACT: use local-date components (not toISOString/UTC) to avoid day-shift outside UTC —
  // expires_at is a timezone-free date-only value; converting via UTC causes off-by-one for
  // users in UTC+ timezones near midnight.
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * AK-6 CONTRACT: The initial InternalPhase after Close must never hold a plaintext key.
 * This function returns the reset phase value that handleClose sets.
 */
export function clearedPhase(): { name: 'CREATE' } {
  return { name: 'CREATE' };
}

/**
 * Formats a date string for display in de-DE locale.
 * CONTRACT: date-only values (YYYY-MM-DD) are timezone-free; parse as local midnight to avoid
 * CONTRACT: UTC day-shift for users in UTC+ timezones. Full ISO timestamps (with T/Z) use new Date() directly.
 * CONTRACT: null / empty string → returns '—'.
 */
export function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  const dateOnlyRe = /^\d{4}-\d{2}-\d{2}$/;
  const d = dateOnlyRe.test(dateStr)
    ? (() => {
        const [y, m, day] = dateStr.split('-').map(Number);
        return new Date(y, m - 1, day);
      })()
    : new Date(dateStr);
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}
